import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import BorderAll from "@mui/icons-material/BorderAll";
import BorderStyle from "@mui/icons-material/BorderStyle";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import PaletteOutlined from "@mui/icons-material/PaletteOutlined";
import {
  Box,
  Button,
  FormControlLabel,
  Menu,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import { useCallback, useRef, useState, type FC } from "react";
import { usePopover } from "../../../../../../utils/hooks/use-popover";
import { ColourPicker } from "../../../../../generic/components/colour-picker/colour-picker";
import { SET_DATA_TABLE_BORDER_COMMAND } from "../../../types/commands";
import type { Border } from "../../../types/data-table-border";

export const DataTableBorder: FC<{
  nodeKey: string;
  border: Border;
}> = ({ nodeKey, border: initialBorder }) => {
  const { anchorRef, handleToggle, handleClose, open } = usePopover<HTMLButtonElement>();
  const [border, setTempBorder] = useState<{ value: Border; skipHistory: boolean }>({
    value: initialBorder,
    skipHistory: false,
  });
  const [editor] = useLexicalComposerContext();

  const handleBorderWidthChange = useCallback(
    (value: number, skipHistory?: boolean) => {
      const newBorder = { ...border.value, width: value };
      editor.dispatchCommand(SET_DATA_TABLE_BORDER_COMMAND, {
        nodeKey,
        border: newBorder,
        skipHistory: skipHistory ?? false,
      });
      setTempBorder({
        value: newBorder,
        skipHistory: skipHistory ?? false,
      });
    },
    [border, setTempBorder, editor, nodeKey],
  );

  const handleBorderStyleChange = useCallback(
    (value: string) => {
      const newBorder = { ...border.value, style: value };
      editor.dispatchCommand(SET_DATA_TABLE_BORDER_COMMAND, {
        nodeKey,
        border: newBorder,
        skipHistory: false,
      });
      setTempBorder({
        value: newBorder,
        skipHistory: false,
      });
    },
    [border, setTempBorder, editor, nodeKey],
  );

  const handleBorderColourChange = useCallback(
    (value: string, skipHistory?: boolean) => {
      const newBorder = { ...border.value, colour: value };
      editor.dispatchCommand(SET_DATA_TABLE_BORDER_COMMAND, {
        nodeKey,
        border: newBorder,
        skipHistory: skipHistory ?? false,
      });
      setTempBorder({
        value: newBorder,
        skipHistory: skipHistory ?? false,
      });
    },
    [border, setTempBorder, editor, nodeKey],
  );

  return (
    <>
      <Button
        data-analytics-id="lexical-table-menu-borders"
        onClick={handleToggle}
        ref={anchorRef}
        endIcon={
          <KeyboardArrowDown
            sx={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          />
        }
        sx={{ color: "text.secondary" }}
        size="small"
      >
        {t("Borders")}
      </Button>
      <Menu
        // eslint-disable-next-line react-hooks/refs
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { minWidth: 200 },
          },
        }}
      >
        <Stack sx={{ p: 1 }}>
          <Stack direction="row" alignItems="center" gap={1} justifyContent="inline-start">
            <BorderAll fontSize="small" />
            <Typography variant="body1" color="text.secondary">
              {t("Border width")}
            </Typography>
          </Stack>

          <Box sx={{ px: 1 }}>
            <Slider
              value={border.value.width}
              onChange={(_, value) => handleBorderWidthChange(value, true)}
              onChangeCommitted={(_, value) => handleBorderWidthChange(value, false)}
              min={1}
              max={10}
              step={1}
              size="medium"
              valueLabelFormat={(val) => `${val}px`}
              valueLabelDisplay="auto"
            />
          </Box>
          <Stack
            sx={{ mt: 2 }}
            direction="row"
            alignItems="center"
            gap={1}
            justifyContent="inline-start"
          >
            <BorderStyle fontSize="small" />
            <Typography variant="body1" color="text.secondary">
              {t("Border style")}
            </Typography>
          </Stack>
          <RadioGroup
            row
            onChange={(e) => handleBorderStyleChange(e.target.value)}
            value={border.value.style}
            sx={{ px: 1 }}
          >
            <FormControlLabel
              value="solid"
              control={<Radio size="small" />}
              label={t("Solid")}
              sx={{ color: "text.secondary" }}
            />
            <FormControlLabel
              value="dashed"
              control={<Radio size="small" />}
              label={t("Dashed")}
              sx={{ color: "text.secondary" }}
            />
            <FormControlLabel
              value="dotted"
              control={<Radio size="small" />}
              label={t("Dotted")}
              sx={{ color: "text.secondary" }}
            />
          </RadioGroup>

          <Box sx={{ mt: 2 }}>
            <BorderColourPicker colour={border.value.colour} setColour={handleBorderColourChange} />
          </Box>
        </Stack>
      </Menu>
    </>
  );
};

const BorderColourPicker: FC<{
  colour: string;
  setColour: (colour: string, skipHistory?: boolean) => void;
}> = ({ colour, setColour }) => {
  const popover = usePopover<HTMLButtonElement>();
  const popoverInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Button
        variant="text"
        data-analytics-id="lexical-table-menu-border-colour"
        // eslint-disable-next-line react-hooks/refs
        onClick={popover.handleToggle}
        // eslint-disable-next-line react-hooks/refs
        ref={popover.anchorRef}
        sx={{ color: "text.secondary", justifyContent: "flex-start", pl: 1, width: "100%" }}
        startIcon={<PaletteOutlined fontSize="small" />}
      >
        <Typography variant="body1">{t("Border color")}</Typography>
      </Button>
      <ColourPicker
        // eslint-disable-next-line react-hooks/refs
        popover={popover}
        colour={colour}
        onChange={setColour}
        inputRef={popoverInputRef}
      />
    </>
  );
};
